import * as React from 'react';
import { useDropzone } from 'react-dropzone';

import { cn, getText } from '../../helpers';
import abbvAudio from '../../assets/audio/abbv_agn_drugs_shortened.mp3';
import accentedAudio from '../../assets/audio/accented_speech.mp3';
import catAudio from '../../assets/audio/cat_earnings_intro.mp3';
import usePrevious from './usePrevious';
import useTranscriptionRequest from './useTranscriptionRequest';
import Loader from '../_common/loader';
import APIManager from '../../utilities/api-manager';

const AUDIO_SAMPLES = [
    {
        name: 'Poor Audio Quality',
        path: abbvAudio
    },
    {
        name: 'Accented Speech',
        path: accentedAudio
    },
    {
        name: 'Industry Specific Jargon',
        path: catAudio
    }
];

function Transcript(props: TranscriptProps): JSX.Element {
    const { value, onTokenClick } = props;

    // Use spans so that consecutive transcripts are inlined together
    return (
        <span>
            {value.map(token => (onTokenClick ? (
                <React.Fragment key={token.start_ms}>
                    <span
                        role="button"
                        tabIndex={0}
                        className="scribe-demo__token"
                        onClick={() => onTokenClick(token)}
                        onKeyPress={e => (e.key === 'Enter' ? onTokenClick(token) : null)}
                    >
                        {token.transcript}
                    </span>{' '}
                </React.Fragment>
            ) : (
                <React.Fragment key={token.start_ms}>{token.transcript} </React.Fragment>
            )))}
        </span>
    );
}

export default function KenshoScribeDemo(props: KenshoScribeDemoProps): JSX.Element {
    const { accessToken, onUnauthorizedResponse, onLoaded } = props;
    const [speechFile, setSpeechFile] = React.useState<File | null>(null);
    const [speechSrc, setSpeechSrc] = React.useState<string | null>(null);
    const prevSpeechSrc = usePrevious(speechSrc);
    const audioRef = React.useRef<HTMLAudioElement | null>(null);
    const { resetRequest, status, transcript, transcribeSpeechFile, error } = useTranscriptionRequest(accessToken, onUnauthorizedResponse);

    React.useEffect(() => {
        if (audioRef.current != null && prevSpeechSrc !== null && prevSpeechSrc !== speechSrc) {
            audioRef.current.load();
        }
    });

    const resetAudioAndTranscript = React.useCallback(() => {
        resetRequest();
        setSpeechSrc(null);
        setSpeechFile(null);
    }, []);

    const createAudioPreview = React.useCallback(file => {
        const reader = new FileReader();
        reader.onload = () => {
            if (reader.result) setSpeechSrc(reader.result as string);
        };
        reader.readAsDataURL(file);
    }, []);

    const goToAudio = React.useCallback((ref: React.RefObject<HTMLAudioElement | null>, time: number) => {
        const audioEl = ref.current;
        if (audioEl) {
            audioEl.currentTime = time / 1000;
            audioEl.play();
        }
    }, []);

    const handleFileDrop = React.useCallback(
        files => {
            resetAudioAndTranscript();
            createAudioPreview(files[0]);
            setSpeechFile(files[0]);
        },
        [resetAudioAndTranscript]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: handleFileDrop,
        // Specify both mpeg and mp3 MIME types in order to support both Chrome and Firefox
        accept: 'audio/mpeg, audio/mp3'
    });

    function handleExampleClick(filename: string): void {
        setSpeechSrc(filename);
        APIManager.getAudioFile(filename).then(response => {
            const file = new Blob([response.data], { type: 'audio/mpeg' }) as File;
            setSpeechFile(file);
        });
    }

    function handleTranscribeClick(): void {
        if (speechFile != null) transcribeSpeechFile(speechFile);
    }

    const isPreviewDisabled = transcript != null || status === 'initiated' || status === 'loading';
    const isTranscribeDisabled = speechFile == null || isPreviewDisabled;

    React.useEffect(() => {
        if (onLoaded) onLoaded();
    }, []);

    return (
        <section className="scribe-demo">
            <div className="scribe-demo__input">
                <h2 className="subheading subheading--with-border margin-top-m">{getText('pages.solution.demos.kenshoScribe.inputHeader')}</h2>
                {speechFile === null && speechSrc === null && (
                    <div className={cn('scribe-demo__fileInputs', { 'scribe-demo__dragging': isDragActive })}>
                        <div className="scribe-demo__fileUpload" {...getRootProps()}>
                            <input {...getInputProps()} />
                            <div className="scribe-demo__fileUploadText">
                                {getText(`pages.solution.demos.kenshoScribe.${isDragActive ? 'dropHere' : 'dragHere'}`)}
                            </div>
                        </div>
                    </div>
                )}
                {speechSrc !== null && (
                    <>
                        <div className="scribe-demo__preview">
                            <audio controls ref={audioRef}>
                                <source src={speechSrc} type="audio/mpeg" />
                                <track default kind="captions" />
                            </audio>
                        </div>
                        <div className="scribe-demo__actions">
                            <button className="scribe-demo__example-link" onClick={resetAudioAndTranscript} type="button">
                                {getText('pages.solution.demos.kenshoScribe.clear')}
                            </button>
                            <button className="button button--primary" disabled={isTranscribeDisabled} onClick={handleTranscribeClick} type="button">
                                {getText('pages.solution.demos.kenshoScribe.transcribe')}
                            </button>
                        </div>
                    </>
                )}
                <h2 className="subheading subheading--with-border margin-top-m">{getText('pages.solution.demos.kenshoScribe.samplesHeader')}</h2>
                {AUDIO_SAMPLES.map(sample => (
                    <div key={sample.name} className="scribe-demo__example-links">
                        <span>{sample.name}</span>
                        <button
                            className="scribe-demo__example-link"
                            disabled={isPreviewDisabled}
                            onClick={() => handleExampleClick(sample.path)}
                            type="button"
                        >
                            {getText('pages.solution.demos.kenshoScribe.preview')} <small>(.mp3)</small>
                        </button>
                    </div>
                ))}
            </div>
            <div className="scribe-demo__output">
                <h2 className="subheading subheading--with-border margin-top-m">{getText('pages.solution.demos.kenshoScribe.outputHeader')}</h2>
                {error && <p>{error}</p>}
                {status === 'uninitiated' && (
                    <em className="scribe-demo__transcribePlaceholder">{getText('pages.solution.demos.kenshoScribe.transcriptPlaceholder')}</em>
                )}
                {status !== 'uninitiated' && (
                    <div className="scribe-demo__transcription-pane">
                        {(status === 'loading' || status === 'initiated') && <Loader minHeight="150px" />}
                        {status === 'success' &&
                            transcript &&
                            transcript.slice_meta.map(slice => (
                                <Transcript
                                    key={`${slice.start_ms}${slice.duration_ms}`}
                                    value={slice.token_meta}
                                    onTokenClick={token => goToAudio(audioRef, token.start_ms)}
                                />
                            ))}
                    </div>
                )}
            </div>
        </section>
    );
}
